import { useCallback, useEffect, useState } from "react";
import PageTitle from "../components/PageTitle";
import { FLEET_DEFAULT } from "../data/fleetDefault";
import {
  fetchFleetForAdmin,
  saveFleetContent,
  uploadFleetImage,
} from "../api/opsApi";

// Editing the fleet page: the section headings, each card's title and description, and the
// pictures on it.
//
// The whole page is held in one piece of state and saved in one go, which is why there is a
// single Save at the bottom rather than a save on every field. Gated by the portal session and
// the administrator role on the server as well as here.

// A deep copy, so editing never writes through to the default the page falls back to.
const copyOf = (content) => JSON.parse(JSON.stringify(content));

export default function FleetAdminPage() {
  const [content, setContent] = useState(null);
  // Whether the public /fleet page exists at all. Kept beside the words so one Save publishes
  // both, and so turning the page on can never happen without the content being written too.
  const [enabled, setEnabled] = useState(false);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const document = await fetchFleetForAdmin();
      // Nothing saved yet: start from what the page is showing today.
      setContent(copyOf(document.content ?? FLEET_DEFAULT));
      setEnabled(document.enabled === true);
      setSavedAt(document.updatedAt ?? null);
      setDenied(false);
      setDirty(false);
    } catch (error) {
      if (error.status === 401 || error.status === 403) setDenied(true);
      else setMessage({ bad: true, text: error.message });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Leaving with unsaved work is easy to do by accident on a page this long.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Every edit goes through here, so nothing can change the page without marking it unsaved.
  const edit = (change) => {
    setContent((current) => {
      const next = copyOf(current);
      change(next);
      return next;
    });
    setDirty(true);
    setMessage(null);
  };

  const addImages = async (sectionIndex, cardIndex, files) => {
    if (!files?.length) return;
    setBusy(true);
    setMessage(null);
    try {
      const uploaded = [];
      for (const file of files) uploaded.push(await uploadFleetImage(file));
      edit((next) => {
        next.sections[sectionIndex].cards[cardIndex].images.push(...uploaded.map((image) => image.url));
      });
      setMessage({ text: `${uploaded.length} image${uploaded.length === 1 ? "" : "s"} added. Save to publish.` });
    } catch (error) {
      setMessage({ bad: true, text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await saveFleetContent(content, enabled);
      setSavedAt(new Date().toISOString());
      setDirty(false);
      setMessage({
        text: [
          enabled ? "Saved and live." : "Saved. The page is hidden.",
          result.removed ? `${result.removed} unused image${result.removed === 1 ? "" : "s"} removed.` : null,
        ]
          .filter(Boolean)
          .join(" "),
      });
    } catch (error) {
      setMessage({ bad: true, text: error.message });
    } finally {
      setBusy(false);
    }
  };

  if (denied) {
    return (
      <>
        <PageTitle>vWZZ - Fleet admin</PageTitle>
        <div className="page-container shots-admin">
          <h1 className="page-title">Fleet admin</h1>
          <p className="shots-empty">
            Sign in with an administrator account to edit the fleet page.{" "}
            <a href="/interep">Go to sign in</a>
          </p>
        </div>
      </>
    );
  }

  if (!content) {
    return (
      <>
        <PageTitle>vWZZ - Fleet admin</PageTitle>
        <div className="page-container shots-admin">
          <h1 className="page-title">Fleet admin</h1>
          <p className="shots-empty">Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle>vWZZ - Fleet admin</PageTitle>
      <div className="page-container shots-admin fleet-admin">
        <header className="shots-header">
          <h1 className="page-title">Fleet admin</h1>
          <p className="page-subtitle">
            The headings, the card titles and descriptions, and the pictures on each card.
          </p>
        </header>

        <div className="fleet-admin-bar">
          <span>
            {savedAt ? `Last saved ${new Date(savedAt).toLocaleString("en-GB")}` : "Not saved yet"}
            {dirty ? " · unsaved changes" : ""}
          </span>
          <div className="shots-form-actions">
            {message ? (
              <span className={`shots-message ${message.bad ? "is-bad" : ""}`} role="status">
                {message.text}
              </span>
            ) : null}
            <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty}>
              {busy ? "Working..." : "Save page"}
            </button>
          </div>
        </div>

        <section className="fleet-admin-block">
          <h2>The page itself</h2>
          <label className="fleet-admin-switch">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => {
                setEnabled(event.target.checked);
                setDirty(true);
                setMessage(null);
              }}
            />
            <span>
              <strong>{enabled ? "Live" : "Hidden"}</strong>
              {enabled
                ? " vwzz.eu/fleet is open to everyone and the hangar is in the navigation."
                : " vwzz.eu/fleet sends visitors to the home page and the hangar is out of the navigation."}
            </span>
          </label>
          <p className="shots-note">
            Takes effect when the page is saved. Everything below can be edited either way.
          </p>
        </section>

        <section className="fleet-admin-block">
          <h2>Intro</h2>
          <label>
            <span>Heading</span>
            <input
              type="text"
              maxLength={120}
              value={content.intro.title}
              onChange={(event) => edit((next) => { next.intro.title = event.target.value; })}
            />
          </label>
          {content.intro.paragraphs.map((paragraph, index) => (
            <label key={index}>
              <span>Paragraph {index + 1}</span>
              <textarea
                rows={3}
                maxLength={1200}
                value={paragraph}
                onChange={(event) => edit((next) => { next.intro.paragraphs[index] = event.target.value; })}
              />
            </label>
          ))}
        </section>

        {content.sections.map((section, sectionIndex) => (
          <section className="fleet-admin-block" key={sectionIndex}>
            <div className="fleet-admin-row">
              <label>
                <span>Section heading</span>
                <input
                  type="text"
                  maxLength={120}
                  value={section.title}
                  onChange={(event) => edit((next) => { next.sections[sectionIndex].title = event.target.value; })}
                />
              </label>
              <label>
                <span>Platform</span>
                <input
                  type="text"
                  maxLength={80}
                  value={section.platform}
                  onChange={(event) => edit((next) => { next.sections[sectionIndex].platform = event.target.value; })}
                />
              </label>
            </div>

            {section.cards.map((card, cardIndex) => (
              <article className="fleet-admin-card" key={cardIndex}>
                <label>
                  <span>Card title</span>
                  <input
                    type="text"
                    maxLength={120}
                    value={card.title}
                    onChange={(event) => edit((next) => { next.sections[sectionIndex].cards[cardIndex].title = event.target.value; })}
                  />
                </label>
                <label>
                  <span>Description</span>
                  <textarea
                    rows={3}
                    maxLength={1200}
                    value={card.text}
                    onChange={(event) => edit((next) => { next.sections[sectionIndex].cards[cardIndex].text = event.target.value; })}
                  />
                </label>

                <span className="fleet-admin-label">Pictures ({card.images.length})</span>
                <div className="fleet-admin-images">
                  {card.images.map((image, imageIndex) => (
                    <figure key={image}>
                      <img src={image} alt="" loading="lazy" />
                      <div>
                        <button
                          type="button"
                          disabled={busy || imageIndex === 0}
                          aria-label="Move earlier"
                          onClick={() => edit((next) => {
                            const list = next.sections[sectionIndex].cards[cardIndex].images;
                            [list[imageIndex - 1], list[imageIndex]] = [list[imageIndex], list[imageIndex - 1]];
                          })}
                        >
                          &larr;
                        </button>
                        <button
                          type="button"
                          disabled={busy || imageIndex === card.images.length - 1}
                          aria-label="Move later"
                          onClick={() => edit((next) => {
                            const list = next.sections[sectionIndex].cards[cardIndex].images;
                            [list[imageIndex], list[imageIndex + 1]] = [list[imageIndex + 1], list[imageIndex]];
                          })}
                        >
                          &rarr;
                        </button>
                        <button
                          type="button"
                          className="is-danger"
                          disabled={busy}
                          aria-label="Remove picture"
                          onClick={() => edit((next) => {
                            next.sections[sectionIndex].cards[cardIndex].images.splice(imageIndex, 1);
                          })}
                        >
                          &times;
                        </button>
                      </div>
                    </figure>
                  ))}
                </div>

                <label className="fleet-admin-add">
                  <span>Add pictures (JPEG or PNG)</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    disabled={busy}
                    onChange={(event) => {
                      addImages(sectionIndex, cardIndex, Array.from(event.target.files ?? []));
                      event.target.value = "";
                    }}
                  />
                </label>
              </article>
            ))}
          </section>
        ))}

        <div className="fleet-admin-bar">
          <span>
            Pictures removed from a card are deleted from the server when the page is saved.
          </span>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty}>
            {busy ? "Working..." : "Save page"}
          </button>
        </div>
      </div>
    </>
  );
}
